import { forkJoin, Observable, Subject, throwError, of, EMPTY } from 'rxjs';
import {
  takeUntil,
  catchError,
  tap,
  map,
  delay,
  retryWhen,
  take,
  switchMap,
  filter,
} from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { MxEventName, MxPropertyName } from '@axon-enterprise/track';
import { SnAuditService } from '@lib/api/services';
import { ISnAuthConfig } from '@lib/auth';
import { SnCurrentOrganization, SnCurrentUser, SnSessionService } from '@lib/auth';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { MixpanelService } from '@lib/mixpanel';
import { LanguageSelectionService } from '@lib/translation';

import { SN_AUTH_CONFIG } from '../../providers';
import { AxonDisclaimerComponent } from '../axon-disclaimer';
import { CjisComplianceComponent } from '../cjis-compliance';

@Component({
  template: '',
  styleUrls: ['./sso-success.view.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCardModule],
})
export class SsoSuccessView implements OnInit, OnDestroy {
  private _destroy$ = new Subject();

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _sessionService: SnSessionService,
    private readonly _router: Router,
    private readonly _mixpanelService: MixpanelService,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _matDialog: MatDialog,
    private readonly _currentUser: SnCurrentUser,
    private readonly _auditService: SnAuditService,
    private readonly _datadogService: FsDataDogRumService,
    private readonly _languageSelection: LanguageSelectionService,
    @Inject(SN_AUTH_CONFIG) private readonly _config: ISnAuthConfig,
  ) {}

  public ngOnInit(): void {
    this._route.queryParams
      .pipe(
        map((params) => {
          const { token, provider, redirect_uri: redirectUri } = params;
          return { token, provider, redirectUri };
        }),
        tap(({ token, provider }) => {
          this._sessionService.setToken(token);
          if (provider) {
            this._sessionService.setSsoProvider(provider);
          }
        }),
        switchMap(({ redirectUri }) => this._getUserProfile().pipe(map(() => ({ redirectUri })))),
        tap({
          next: () => {
            this._datadogService.addTiming('sso_success');
            this._languageSelection
              .initialize(this._currentUser)
              .pipe(
                takeUntil(this._destroy$),
                catchError(err => {
                  this.logToMixpanel(err);
                  console.warn('Language initialization failed', err);
                  return EMPTY;
                })
              )
              .subscribe();
          },
        }),
        switchMap(({ redirectUri }) =>
          this._checkCJISCompliance().pipe(map(() => ({ redirectUri }))),
        ),
        tap(() => {
          this._sessionService.setCjisApprovedKey();
        }),
        switchMap(({ redirectUri }) =>
          this._openAxonDisclaimerDialog().pipe(map(() => ({ redirectUri }))),
        ),
        catchError((error) => {
          this.logToMixpanel(error);
          return throwError(() => error);
        }),
        takeUntil(this._destroy$),
      )
      .subscribe(({ redirectUri }) => {
        this.logToMixpanel();
        void this._router.navigateByUrl(
          this.getRedirectUrl(this._config.redirectAuthorizedTo, redirectUri),
        );
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _checkCJISCompliance(): Observable<unknown> {
    if (this._currentOrganization.settings.isCompliance) {
      return this._openCJISDialog();
    }

    return of(null);
  }

  private _openCJISDialog(): Observable<boolean> {
    const dialog = this._matDialog.open(CjisComplianceComponent, {
      data: {
        message: this._currentOrganization.settings.complianceMessage,
        title: this._currentOrganization.settings.complianceTitle,
      },
      panelClass: 'my-custom-dialog-class',
      disableClose: true,
    });
    return dialog.afterClosed().pipe(
      filter((result) => !!result),
      tap(() => {
        this._auditService
          .createDisclaimerAudit({
            disclaimer: 'CJIS',
            userId: this._currentUser.id,
            userName: this._currentUser.fullName,
          })
          .subscribe();
      }),
      takeUntil(this._destroy$),
    );
  }

  private _openAxonDisclaimerDialog(): Observable<unknown> {
    if (
      this._sessionService.isAxonSso &&
      this._currentOrganization.settings.axonWarningEnabled
    ) {
      const dialog = this._matDialog.open(AxonDisclaimerComponent, {
        data: {
          message: this._currentOrganization.settings.axonWarningDescription,
          title: this._currentOrganization.settings.axonWarningTitle,
        },
        panelClass: 'axon-disclaimer-dialog-class',
        disableClose: true,
      });
      return dialog.afterClosed().pipe(
        tap((confirm) => {
          if (!confirm) {
            this._sessionService.removeCjisApprovedKey();
          }
        }),
        tap(() => {
          this._auditService
            .createDisclaimerAudit({
              disclaimer: 'AXON',
              userId: this._currentUser.id,
              userName: this._currentUser.fullName,
            })
            .subscribe();
        }),
        takeUntil(this._destroy$),
      );
    }
    return of(null);
  }

  private _getUserProfile(): Observable<unknown> {
    return forkJoin([this._currentUser.getProfile(), this._currentOrganization.getProfile()]).pipe(
      catchError((err) => {
        return throwError(err);
      }),
      retryWhen((err) => {
        return err.pipe(delay(3000), take(3));
      }),
      takeUntil(this._destroy$),
    );
  }

  private getRedirectUrl(fallbackUrl: string, dynamicUrl: string): string {
    // Protect against the escalation path of an arbitrary redirect
    if (dynamicUrl && /^\/(?!\/)/.test(dynamicUrl)) {
      return dynamicUrl;
    }
    return fallbackUrl;
  }

  private logToMixpanel(error?: any): void {
    const { provider, redirect_uri: redirectUri } = this._route.snapshot.queryParams;

    const hasError = !!error;

    this._mixpanelService.trackMetric(MxEventName.FususLogin, {
      [MxPropertyName.LoginMethod]: 'sso',
      [MxPropertyName.Source]: provider,
      [MxPropertyName.RedirectTo]: redirectUri,
      [MxPropertyName.HasError]: hasError,
      ...(hasError ? { [MxPropertyName.ErrorReason]: error.message || error.toString() } : {}),
    });
  }
}
